import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { KeyRound, Eye, EyeOff, ShieldAlert } from 'lucide-react';
import { useT } from '@/store/i18n';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { toast } from '@/components/ui/Toast';
import { api } from '@/lib/api';
import type { User, Role } from '@/types';

const ROLE_TONE: Record<Role, 'brand' | 'info' | 'warning' | 'neutral'> = {
  admin: 'warning',
  academic_admin: 'info',
  teacher: 'brand',
  executive: 'neutral',
};

interface Props {
  open: boolean;
  onClose: () => void;
  targetUser: User | null;
}

export function AdminResetPasswordModal({ open, onClose, targetUser }: Props) {
  const t = useT();
  const [newPassword, setNewPassword]       = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [adminPassword, setAdminPassword]   = useState('');
  const [showPwd, setShowPwd]               = useState(false);
  const [fieldError, setFieldError]         = useState('');

  function resetFields() {
    setNewPassword('');
    setConfirmPassword('');
    setAdminPassword('');
    setShowPwd(false);
    setFieldError('');
  }

  function handleClose() {
    resetFields();
    onClose();
  }

  const submit = useMutation({
    mutationFn: () =>
      api('users/reset-password.php', {
        method: 'POST',
        json: {
          user_id:        targetUser!.id,
          new_password:   newPassword,
          admin_password: adminPassword,
        },
      }),
    onSuccess: () => {
      toast.success(t('pwd.reset.success'));
      resetFields();
      onClose();
    },
    onError: (e: Error) => {
      // Surface a field-level error so it's visible inside the modal without
      // navigating away; also show a toast for accessibility.
      setFieldError(e.message);
      toast.error(t('pwd.reset.fail'), e.message);
    },
  });

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFieldError('');

    if (newPassword.length < 8) {
      setFieldError(t('pwd.reset.val.short'));
      return;
    }
    if (newPassword !== confirmPassword) {
      setFieldError(t('pwd.reset.val.mismatch'));
      return;
    }
    if (!adminPassword) {
      setFieldError(t('pwd.reset.val.admin_required'));
      return;
    }

    submit.mutate();
  }

  if (!targetUser) return null;

  const pwdType = showPwd ? 'text' : 'password';
  const eyeBtn = (
    <button
      type="button"
      onClick={() => setShowPwd((v) => !v)}
      className="pointer-events-auto text-slate-400 hover:text-slate-700"
      aria-label={showPwd ? t('password.hide') : t('password.show')}
    >
      {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
    </button>
  );

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={
        <span className="flex items-center gap-2">
          <KeyRound className="w-5 h-5 text-amber-500" />
          {t('pwd.reset.title')}
        </span>
      }
      description={t('pwd.reset.desc')}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={handleClose}>{t('btn.cancel')}</Button>
          <Button
            type="submit"
            form="admin-reset-pwd-form"
            loading={submit.isPending}
            leftIcon={<KeyRound className="w-4 h-4" />}
          >
            {t('pwd.reset.btn')}
          </Button>
        </>
      }
    >
      {/* Target user info banner */}
      <div className="mb-5 flex items-center gap-3 rounded-lg bg-slate-50 border border-slate-200 px-4 py-3">
        <ShieldAlert className="w-5 h-5 text-amber-500 flex-shrink-0" />
        <div className="min-w-0">
          <div className="text-sm font-semibold text-slate-900 truncate">{targetUser.full_name}</div>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-xs text-slate-500">@{targetUser.username}</span>
            <Badge tone={ROLE_TONE[targetUser.role]}>{t(`role.${targetUser.role}`)}</Badge>
          </div>
        </div>
      </div>

      <form id="admin-reset-pwd-form" onSubmit={onSubmit} className="space-y-4">
        <Input
          label={t('pwd.reset.new_password')}
          type={pwdType}
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          hint={t('pwd.reset.hint')}
          required
          autoFocus
          autoComplete="new-password"
          rightIcon={eyeBtn}
        />
        <Input
          label={t('pwd.reset.confirm_password')}
          type={pwdType}
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          required
          autoComplete="new-password"
        />
        <div className="border-t border-slate-100 pt-4">
          <Input
            label={t('pwd.reset.admin_password')}
            type="password"
            value={adminPassword}
            onChange={(e) => setAdminPassword(e.target.value)}
            hint={t('pwd.reset.admin_password_hint')}
            required
            autoComplete="current-password"
          />
        </div>

        {/* Inline validation / server error */}
        {fieldError && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
            {fieldError}
          </p>
        )}
      </form>
    </Modal>
  );
}
